// 사진 라이브러리 — 팔레트 '사진' 을 누르면 저장된 사진을 고른다(오너 요청 2026-09-22).
//
// · 사용자가 불러온 사진은 **자동으로** 여기 쌓이고, 사용자가 지울 때만 사라진다.
// · 썸네일을 **누르면** 놓기 모드(캔버스를 눌러 놓는다), **끌어다 놓아도** 된다.
//   그래서 모달이 아니라 오른쪽 패널에 산다 — 모달이 캔버스를 덮으면 끌어다 놓을 수 없다.
// · 표 칸에 넣을 때(`mode: 'pick'`)만 모달로 뜬다.

import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SignatureLayoutAssetDto } from '@bhops/shared';
import { signatureLayoutAssetsApi } from '@/api/signatureLayoutAssets';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import { Icon } from '@/components/Icon';

/** 끌어다 놓을 때 dataTransfer 에 싣는 형식. 캔버스 오버레이가 이 이름으로 읽는다. */
export const ASSET_DRAG_MIME = 'application/x-bhops-layout-asset';

export interface AssetDragPayload {
  s3Key: string;
  fileName: string;
  url: string;
  /** 원본 픽셀 크기(썸네일이 다 읽힌 뒤에만 안다). 없으면 기본 크기로 놓는다. */
  naturalW?: number;
  naturalH?: number;
}

export const LAYOUT_ASSETS_QUERY_KEY = ['signature-layout-assets'] as const;

interface Props {
  mode: 'panel' | 'pick';
  /** 고른(또는 방금 올린) 사진. panel 모드에서는 '놓기 모드' 로 들어간다는 뜻이다. */
  onPick: (asset: AssetDragPayload) => void;
  onClose?: () => void;
  /** panel 모드 — 지금 놓기 대기 중인 사진(강조 표시). */
  activeKey?: string | null;
  /** 고르면 어디로 가는지 알려 줄 때(예: 칸을 골라 둔 상태). */
  targetHint?: string;
}

export function LayoutAssetLibrary({ mode, onPick, onClose, activeKey, targetHint }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<SignatureLayoutAssetDto | null>(null);
  const sizes = useRef<Record<string, { w: number; h: number }>>({});

  const {
    data: assets = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: LAYOUT_ASSETS_QUERY_KEY,
    queryFn: () => signatureLayoutAssetsApi.list(),
    // presigned URL 이 짧게 유효하다 — 오래 캐시하지 않는다.
    staleTime: 30_000,
  });

  /** 여러 장 올리기 진행(몇 장 중 몇 장째). null 이면 쉬는 중. */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  // 여러 장을 한 번에(2026-09-23 오너 요청). 한 장씩 차례로 올린다 — 동시에 올리면 느린 회선에서
  // presigned 주소(15분)가 먼저 만료될 수 있고, 어느 파일이 실패했는지도 가리기 어렵다.
  // 한 장이 실패해도 나머지는 계속 올리고, 끝나면 실패한 파일을 모아 알려 준다.
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const ok: SignatureLayoutAssetDto[] = [];
      const failed: string[] = [];
      setProgress({ done: 0, total: files.length });
      for (const [i, f] of files.entries()) {
        try {
          ok.push(await signatureLayoutAssetsApi.upload(f));
        } catch (e) {
          failed.push(`${f.name}(${e instanceof Error ? e.message : extractApiError(e)})`);
        }
        setProgress({ done: i + 1, total: files.length });
      }
      return { ok, failed };
    },
    onSuccess: ({ ok, failed }) => {
      void qc.invalidateQueries({ queryKey: LAYOUT_ASSETS_QUERY_KEY });
      if (ok.length > 0) {
        toast.success(
          ok.length === 1
            ? `'${ok[0].fileName}' 을 사진 팔레트에 저장했습니다.`
            : `사진 ${ok.length}장을 사진 팔레트에 저장했습니다.`,
        );
      }
      if (failed.length > 0) toast.error(failed.join(', '), `사진 ${failed.length}장 올리기 실패`);
      // 한 장만 올렸으면 바로 쓰려는 것이다 — 고른 것으로 친다. 여러 장이면 팔레트에 쌓아만
      // 두고 사람이 고른다(어느 것을 놓을지 모른다).
      if (ok.length === 1 && singleFileRef.current) onPick(toPayload(ok[0], undefined));
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : extractApiError(e), '사진 올리기 실패'),
    onSettled: () => setProgress(null),
  });
  /** 이번에 고른 파일이 한 장이었는가 — 한 장일 때만 올린 사진을 바로 고른다. */
  const singleFileRef = useRef(false);

  const archive = useMutation({
    mutationFn: (id: string) => signatureLayoutAssetsApi.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LAYOUT_ASSETS_QUERY_KEY });
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(extractApiError(e), '사진 지우기 실패'),
  });

  function toPayload(
    a: SignatureLayoutAssetDto,
    size: { w: number; h: number } | undefined,
  ): AssetDragPayload {
    return {
      s3Key: a.s3Key,
      fileName: a.fileName,
      url: a.url,
      naturalW: size?.w,
      naturalH: size?.h,
    };
  }

  function onDragStart(e: DragEvent, a: SignatureLayoutAssetDto) {
    e.dataTransfer.setData(ASSET_DRAG_MIME, JSON.stringify(toPayload(a, sizes.current[a.s3Key])));
    e.dataTransfer.effectAllowed = 'copy';
  }

  // 탐색기에서 사진 파일을 여러 장 끌어다 놓으면 모두 올린다(2026-09-23 오너 요청).
  const [fileOver, setFileOver] = useState(false);
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const onFilesDrop = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setFileOver(false);
    const all = Array.from(e.dataTransfer.files);
    const files = all.filter((f) => f.type === 'image/png' || f.type === 'image/jpeg');
    if (all.length > files.length) {
      toast.error(`PNG나 JPEG가 아닌 파일 ${all.length - files.length}개는 건너뛰었습니다.`);
    }
    if (files.length === 0 || upload.isPending) return;
    singleFileRef.current = files.length === 1;
    upload.mutate(files);
  };

  const body = (
    <div
      data-testid="asset-drop-zone"
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setFileOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setFileOver(false);
      }}
      onDrop={onFilesDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 12,
        minHeight: 0,
        outline: fileOver ? '2px dashed var(--fg-brand)' : undefined,
        outlineOffset: -4,
        background: fileOver ? 'color-mix(in srgb, var(--fg-brand) 6%, transparent)' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>사진</div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="닫기" style={ghostBtn}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {targetHint ??
          (mode === 'panel'
            ? '사진을 누르고 종이를 누르면 놓입니다. 끌어다 놓아도 됩니다. 표 칸 위에 놓으면 그 칸 크기에 맞춰 들어가고, Alt 를 누르고 놓으면 표 위에 자유롭게 놓입니다.'
            : '칸에 넣을 사진을 고르세요.')}
      </p>

      <button
        type="button"
        data-testid="asset-upload"
        onClick={() => fileRef.current?.click()}
        disabled={upload.isPending}
        style={{
          padding: '8px 0',
          border: '1px dashed var(--fg-brand)',
          borderRadius: 'var(--r-btn)',
          background: 'transparent',
          color: 'var(--fg-brand)',
          fontWeight: 600,
          fontSize: 12.5,
          cursor: 'pointer',
        }}
      >
        {upload.isPending
          ? progress && progress.total > 1
            ? `올리는 중… (${progress.done}/${progress.total})`
            : '올리는 중…'
          : '+ 사진 불러오기, 여러 장 끌어다 놓기도 됩니다'}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        style={{ display: 'none' }}
        data-testid="asset-file-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length === 0) return;
          singleFileRef.current = files.length === 1;
          upload.mutate(files);
        }}
      />

      {isLoading && <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>불러오는 중…</p>}
      {isError && (
        <p style={{ fontSize: 12, color: 'var(--fg-danger)' }}>사진 목록을 불러오지 못했습니다.</p>
      )}
      {!isLoading && assets.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          저장된 사진이 없습니다. 위에서 불러오세요.
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
          overflowY: 'auto',
        }}
      >
        {assets.map((a) => {
          const active = activeKey === a.s3Key;
          return (
            <div
              key={a.id}
              data-testid="asset-item"
              draggable={mode === 'panel'}
              onDragStart={(e) => onDragStart(e, a)}
              onClick={() => onPick(toPayload(a, sizes.current[a.s3Key]))}
              title={a.fileName}
              style={{
                position: 'relative',
                border: '2px solid',
                borderColor: active ? 'var(--fg-brand)' : 'var(--border-default)',
                borderRadius: 'var(--r-card)',
                background: 'var(--bg-sunken)',
                cursor: mode === 'panel' ? 'grab' : 'pointer',
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={a.url}
                alt={a.fileName}
                draggable={false}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  sizes.current[a.s3Key] = { w: img.naturalWidth, h: img.naturalHeight };
                }}
                // 썸네일 주소(15분짜리)가 만료되면 목록을 다시 받아 새 주소로 그린다.
                onError={() => void qc.invalidateQueries({ queryKey: LAYOUT_ASSETS_QUERY_KEY })}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  fontSize: 10,
                  padding: '2px 4px',
                  background: 'color-mix(in srgb, var(--bg-surface) 85%, transparent)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {a.fileName}
              </span>
              <button
                type="button"
                aria-label={`${a.fileName} 지우기`}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(a);
                }}
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'color-mix(in srgb, var(--bg-surface) 85%, transparent)',
                  color: 'var(--fg-danger)',
                  cursor: 'pointer',
                  fontSize: 12,
                  lineHeight: '20px',
                }}
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <div
          style={{
            border: '1px solid var(--fg-danger)',
            borderRadius: 'var(--r-card)',
            padding: 10,
            fontSize: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <span>
            '{confirmDelete.fileName}' 을 사진 팔레트에서 지울까요? 이미 놓인 사진은 그대로
            남습니다.
          </span>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" style={ghostBtn} onClick={() => setConfirmDelete(null)}>
              취소
            </button>
            <button
              type="button"
              data-testid="asset-delete-confirm"
              onClick={() => archive.mutate(confirmDelete.id)}
              style={{
                ...ghostBtn,
                background: 'var(--fg-danger)',
                color: 'var(--bg-surface)',
                border: 'none',
              }}
            >
              지우기
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (mode === 'pick') {
    return (
      <div
        role="dialog"
        aria-label="사진 고르기"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          background: 'var(--bg-overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          style={{
            width: 460,
            maxHeight: '80vh',
            overflow: 'auto',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {body}
        </div>
      </div>
    );
  }
  return body;
}

const ghostBtn = {
  padding: '5px 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
  fontSize: 12,
} as const;
